import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useServices } from "@/app/use-services";
import { ApiError, errorMessage } from "@/lib/api/errors";
import { REPORT_REASON_LABEL, REPORT_REASONS, type ReportReason } from "@/lib/api/models/social";
import { BackLink } from "@/ui/BackLink";
import { Button, LinkButton } from "@/ui/Button";
import { ErrorView } from "@/ui/ErrorView";
import { Page } from "@/ui/Page";
import { Row } from "@/ui/Row";
import { Sheet } from "@/ui/Sheet";
import { Body, Display, Label } from "@/ui/Text";
import { Paywall } from "@/features/plan/Paywall";
import { wallForCompatibility } from "@/features/plan/paywall-copy";
import { FRIENDS_QUERY_KEY, friendName } from "./friend-name";

/** Two charts read together, and the actions on a person: accept, remove, block, report. */
export function CompatibilityPage() {
  const { userId = "" } = useParams();
  const { api, analytics } = useServices();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [more, setMore] = useState(false);
  const friends = useQuery({ queryKey: FRIENDS_QUERY_KEY, queryFn: () => api.fetchFriends() });
  const friend = friends.data?.find((f) => f.user_id === userId);
  const name = friend ? friendName(friend) : "them";
  const accepted = friend?.status === "accepted";

  const reading = useQuery({
    queryKey: ["me", "compatibility", userId],
    queryFn: () => api.fetchCompatibility(userId),
    enabled: accepted,
    retry: false,
  });

  const back = () => { void client.invalidateQueries({ queryKey: FRIENDS_QUERY_KEY }); navigate("/friends"); };
  const done = (action: "accept" | "remove" | "block" | "report") => analytics.track({ name: "friend action", action });
  const accept = useMutation({
    mutationFn: () => api.addFriend(userId),
    onSuccess: () => {
      done("accept");
      void client.invalidateQueries({ queryKey: FRIENDS_QUERY_KEY });
    },
  });
  const unfriend = useMutation({ mutationFn: () => api.unfriend(userId), onSuccess: () => { done("remove"); back(); } });
  const block = useMutation({ mutationFn: () => api.blockUser(userId), onSuccess: () => { done("block"); back(); } });
  const report = useMutation({ mutationFn: (reason: ReportReason) => api.reportUser(userId, reason), onSuccess: () => { done("report"); back(); } });

  const head = (
    <div className="flex items-start justify-between">
      <BackLink to="/friends" label="Friends" />
      <button type="button" aria-label="More" onClick={() => setMore(true)} className="label min-h-11 px-2 text-ink">···</button>
    </div>
  );

  return (
    <Page>
      {head}
      <Label className="mt-3">You & {name}</Label>
      {friend && !accepted && (
        <div className="mt-4">
          <Body>{friend.outgoing ? "You’ve invited them. The reading opens once they accept." : "They want to connect. Accept, and the reading between you opens."}</Body>
          {!friend.outgoing && <div className="mt-4"><Button label="Accept" onClick={() => accept.mutate()} busy={accept.isPending} /></div>}
        </div>
      )}
      {accepted && reading.isPending && <Body className="mt-3">Comparing your charts…</Body>}
      {accepted && reading.isError && (
        reading.error instanceof ApiError && reading.error.status === 402
          ? <Paywall copy={wallForCompatibility(name, "Your first friend")} onClose={() => navigate("/friends")} />
          : reading.error instanceof ApiError && reading.error.isNotFound
            ? <Body className="mt-3">They have not set up their chart yet. Once they do, this reading is here.</Body>
            : <ErrorView message={errorMessage(reading.error)} onRetry={() => void reading.refetch()} />
      )}
      {reading.data && (
        <>
          <Display size="md" className="mt-2.5">{reading.data.headline}</Display>
          <div className="mt-1.5">
            <Row label="Runs easily" value={reading.data.runs_easily} serif={false} />
            <Row label="Takes work" value={reading.data.takes_work} serif={false} />
            <Row label="Today, between you" value={reading.data.today} />
          </div>
        </>
      )}
      <Sheet open={more} onClose={() => setMore(false)} label={`Actions for ${name}`}>
        <Label>{name}</Label>
        <div className="mt-3 flex flex-col gap-2">
          {accepted && <Button label="Remove" outlined onClick={() => { setMore(false); unfriend.mutate(); }} />}
          <Button label="Block" outlined onClick={() => { setMore(false); if (window.confirm(`Block ${name}? They will not be able to reach you here.`)) block.mutate(); }} />
          <Label className="mt-3">Report</Label>
          {REPORT_REASONS.map((reason) => (
            <LinkButton key={reason} label={REPORT_REASON_LABEL[reason]} onClick={() => { setMore(false); report.mutate(reason); }} />
          ))}
          <LinkButton label="Cancel" onClick={() => setMore(false)} />
        </div>
      </Sheet>
      {(unfriend.isError || block.isError || report.isError) && (
        <Body className="mt-4">{errorMessage(unfriend.error ?? block.error ?? report.error)}</Body>
      )}
    </Page>
  );
}
